import React, { useState } from "react";
import RelatedDataTabs from "@/components/shared/RelatedDataTabs";
import { EntityAuditFooter } from "@/components/shared/EntityAuditFooter";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpcClient } from "@/lib/trpc";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatMoney, formatDate, cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Printer,
  CheckCircle2,
  Clock,
  Truck,
  Receipt,
  BookOpen,
  DollarSign,
  Loader2,
  Calendar,
  Building2,
} from "lucide-react";
import EntityLink from "@/components/shared/EntityLink";
import { ActionGuard } from "@/components/shared/ActionGuard";

export interface ARReconciliationDetailModalProps {
  reconciliationId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ARReconciliationDetailModal({
  reconciliationId,
  open,
  onOpenChange,
  onSuccess,
}: ARReconciliationDetailModalProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("deliveries");

  const { data, isLoading, error } = useQuery({
    queryKey: ["debtReconciliationDetail", reconciliationId],
    queryFn: async () => {
      if (!reconciliationId) return null;
      return await trpcClient.ar.getDebtReconciliationById.query({
        id: reconciliationId,
      });
    },
    enabled: !!reconciliationId && open,
  });

  const confirmMutation = useMutation({
    mutationFn: async (id: string) => {
      return await trpcClient.ar.confirmDebtReconciliation.mutate({ id });
    },
    onSuccess: () => {
      toast.success("Đã chốt sổ và xác nhận kỳ đối soát thành công!");
      queryClient.invalidateQueries({ queryKey: ["debtReconciliationDetail", reconciliationId] });
      queryClient.invalidateQueries({ queryKey: ["ar-reconciliations"] });
      queryClient.invalidateQueries({ queryKey: ["debtReconciliation"] });
      onSuccess?.();
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi khi xác nhận kỳ đối soát");
    },
  });

  if (!open) return null;

  const reconciliation = data?.reconciliation;
  const customer = data?.customer;
  const deliveries: any[] = data?.deliveries || [];
  const receipts: any[] = data?.receipts || [];
  const ledger: any[] = data?.ledger || [];

  const isConfirmed = reconciliation?.status === "CONFIRMED";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-3xl md:max-w-4xl lg:max-w-4xl xl:max-w-5xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-3" />
            <p className="text-xs text-slate-500 font-medium">
              Đang tải bảng kê chi tiết đối soát công nợ...
            </p>
          </div>
        ) : error || !reconciliation ? (
          <div className="p-8 text-center text-rose-500 text-xs">
            Không tìm thấy thông tin biên bản đối soát.
          </div>
        ) : (
          <>
            {/* Header */}
            {/* Header chuẩn popup EMS (dialog-standard.ts): nền trắng, viền dưới */}
            <div className="bg-card border-b border-border px-6 py-4 pr-12">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <DialogTitle className="font-mono">{reconciliation.code}</DialogTitle>
                    {isConfirmed ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 font-semibold text-[10px]">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Đã Khóa Sổ
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 text-[10px]">
                        <Clock className="w-3 h-3 mr-1" /> Chờ Xác Nhận
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 flex items-center gap-3">
                    <span className="font-semibold text-slate-800 flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5 text-slate-500" />
                      <EntityLink entityType="customer" id={customer?.id || reconciliation.customerId} code={customer?.code} label={customer?.name} />
                    </span>
                    <span className="text-slate-400">•</span>
                    <span className="flex items-center gap-1 text-slate-500">
                      <Calendar className="w-3.5 h-3.5" />
                      Kỳ: Tháng {reconciliation.month}/{reconciliation.year}
                    </span>
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs font-semibold text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                    onClick={() => {
                      window.open(`/finance/ar/reconciliations/${reconciliation.id}/print`, "_blank");
                    }}
                  >
                    <Printer className="w-3.5 h-3.5 mr-1.5" /> In biên bản
                  </Button>

                  {!isConfirmed && (
                    <ActionGuard
                      module="finance"
                      action="approve"
                      fallbackMessage="Chỉ Kế Toán Trưởng hoặc Giám Đốc mới có quyền Chốt Sổ Kỳ Đối Soát"
                    >
                      <Button
                        size="sm"
                        className="h-8 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs"
                        disabled={confirmMutation.isPending}
                        onClick={() => confirmMutation.mutate(reconciliation.id)}
                      >
                        {confirmMutation.isPending ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        ) : (
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                        )}
                        Chốt sổ
                      </Button>
                    </ActionGuard>
                  )}
                </div>
              </div>

              {/* 4 Summary Balance Badges */}
              <div className="grid grid-cols-4 gap-2.5 mt-3 text-xs">
                <div className="bg-white border border-slate-200 rounded-lg p-2">
                  <span className="text-[10px] text-slate-500 block uppercase">1. Nợ Đầu Kỳ</span>
                  <span className="font-mono font-bold text-slate-700 text-xs">
                    {formatMoney(data.openingBalance)} đ
                  </span>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-2">
                  <span className="text-[10px] text-slate-500 block uppercase">2. Giao Hàng (+)</span>
                  <span className="font-mono font-bold text-rose-600 text-xs">
                    +{formatMoney(data.totalIncurred)} đ
                  </span>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-2">
                  <span className="text-[10px] text-slate-500 block uppercase">3. Đã Thu (-)</span>
                  <span className="font-mono font-bold text-emerald-600 text-xs">
                    -{formatMoney(data.totalPaid)} đ
                  </span>
                </div>
                <div className="bg-indigo-50/70 border border-indigo-200 rounded-lg p-2">
                  <span className="text-[10px] text-indigo-700 block font-semibold uppercase">4. Nợ Cuối Kỳ</span>
                  <span className="font-mono font-bold text-indigo-900 text-xs">
                    {formatMoney(data.closingBalance)} đ
                  </span>
                </div>
              </div>
            </div>

            {/* Tabs Content */}
            <div className="flex-1 overflow-y-auto p-4 text-xs">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-3">
                  <TabsTrigger value="deliveries">
                    <Truck className="w-3.5 h-3.5 mr-1.5" />
                    Phiếu Giao Hàng ({deliveries.length})
                  </TabsTrigger>
                  <TabsTrigger value="receipts">
                    <Receipt className="w-3.5 h-3.5 mr-1.5" />
                    Phiếu Thu Tiền ({receipts.length})
                  </TabsTrigger>
                  <TabsTrigger value="ledger">
                    <BookOpen className="w-3.5 h-3.5 mr-1.5" />
                    Bảng Kê Sổ Cái ({ledger.length})
                  </TabsTrigger>
                </TabsList>

                {/* Tab 1: Phiếu Giao Hàng */}
                <TabsContent value="deliveries" className="m-0">
                  {deliveries.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">
                      Không có phiếu giao hàng nào trong kỳ tháng {reconciliation.month}/{reconciliation.year}
                    </div>
                  ) : (
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50/60 text-[11px]">
                            <TableHead className="w-[50px] text-center">STT</TableHead>
                            <TableHead className="w-[120px]">Số Phiếu</TableHead>
                            <TableHead className="w-[100px]">Ngày Giao</TableHead>
                            <TableHead className="w-[100px]">Đơn Hàng</TableHead>
                            <TableHead>Sản Phẩm Giao</TableHead>
                            <TableHead className="text-right w-[120px]">Tổng Tiền</TableHead>
                            <TableHead className="w-[100px] text-center">Trạng Thái</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {deliveries.map((d: any, idx: number) => (
                            <TableRow key={d.id} className="hover:bg-slate-50/50">
                              <TableCell className="text-center font-mono text-slate-400">
                                {idx + 1}
                              </TableCell>
                              <TableCell className="font-mono font-semibold text-slate-700">
                                {d.deliveryId}
                              </TableCell>
                              <TableCell className="text-slate-600">
                                {formatDate(d.deliveryDate)}
                              </TableCell>
                              <TableCell>
                                {d.order?.orderId ? (
                                  <EntityLink
                                    entityType="order"
                                    id={d.order.id || d.orderId}
                                    code={d.order.orderId}
                                  />
                                ) : (
                                  "—"
                                )}
                              </TableCell>
                              <TableCell className="text-slate-700">
                                {(d.deliveryItems || [])
                                  .map((it: any) => `${it.productName} (${it.deliveredQty || 0})`)
                                  .join(", ") || "—"}
                              </TableCell>
                              <TableCell className="text-right font-mono font-bold text-slate-900">
                                {formatMoney(Number(d.totalAmount || 0))} đ
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                                  {d.status}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </TabsContent>

                {/* Tab 2: Phiếu Thu Tiền */}
                <TabsContent value="receipts" className="m-0">
                  {receipts.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">
                      Chưa ghi nhận phiếu thu tiền nào trong kỳ tháng {reconciliation.month}/{reconciliation.year}
                    </div>
                  ) : (
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50/60 text-[11px]">
                            <TableHead className="w-[50px] text-center">STT</TableHead>
                            <TableHead className="w-[130px]">Mã Phiếu Thu</TableHead>
                            <TableHead className="w-[100px]">Ngày Thu</TableHead>
                            <TableHead className="w-[120px]">Phương Thức</TableHead>
                            <TableHead>Nội Dung / Ghi Chú</TableHead>
                            <TableHead className="text-right w-[130px]">Số Tiền Thu</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {receipts.map((r: any, idx: number) => (
                            <TableRow key={r.id} className="hover:bg-slate-50/50">
                              <TableCell className="text-center font-mono text-slate-400">
                                {idx + 1}
                              </TableCell>
                              <TableCell className="font-mono font-semibold text-slate-700">
                                {r.receiptId}
                              </TableCell>
                              <TableCell className="text-slate-600">
                                {formatDate(r.receiptDate)}
                              </TableCell>
                              <TableCell className="text-slate-700">
                                {r.paymentMethod === "CHUYEN_KHOAN"
                                  ? "Chuyển khoản"
                                  : r.paymentMethod === "TIEN_MAT"
                                    ? "Tiền mặt"
                                    : r.paymentMethod}
                              </TableCell>
                              <TableCell className="text-slate-600">
                                {r.notes || r.referenceNumber || "—"}
                              </TableCell>
                              <TableCell className="text-right font-mono font-bold text-emerald-600">
                                {formatMoney(Number(r.amount || 0))} đ
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </TabsContent>

                {/* Tab 3: Bảng Kê Sổ Cái */}
                <TabsContent value="ledger" className="m-0">
                  {ledger.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">
                      Không có giao dịch phát sinh
                    </div>
                  ) : (
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50/60 text-[11px]">
                            <TableHead className="w-[50px] text-center">STT</TableHead>
                            <TableHead className="w-[90px]">Ngày</TableHead>
                            <TableHead className="w-[120px]">Số Chứng Từ</TableHead>
                            <TableHead>Diễn Giải</TableHead>
                            <TableHead className="text-right w-[110px]">Phát Sinh Tăng</TableHead>
                            <TableHead className="text-right w-[110px]">Phát Sinh Giảm</TableHead>
                            <TableHead className="text-right w-[120px] font-bold">Số Dư</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {ledger.map((item: any, idx: number) => (
                            <TableRow key={item.id || idx} className="hover:bg-slate-50/50">
                              <TableCell className="text-center font-mono text-slate-400">
                                {idx + 1}
                              </TableCell>
                              <TableCell className="text-slate-600">
                                {formatDate(item.date)}
                              </TableCell>
                              <TableCell className="font-mono font-medium text-slate-700">
                                {item.refId}
                              </TableCell>
                              <TableCell className="text-slate-700">
                                {item.description}
                              </TableCell>
                              <TableCell className="text-right font-mono text-rose-600">
                                {item.debit > 0 ? `+${formatMoney(item.debit)}` : ""}
                              </TableCell>
                              <TableCell className="text-right font-mono text-emerald-600">
                                {item.credit > 0 ? `-${formatMoney(item.credit)}` : ""}
                              </TableCell>
                              <TableCell className="text-right font-mono font-bold text-slate-900">
                                {formatMoney(item.balance)} đ
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
              <RelatedDataTabs entityType="debt_reconciliation" entityId={reconciliation.id} />
              <EntityAuditFooter entityType="debt_reconciliation" entityId={reconciliation.id} fallback={{ created: reconciliation.createdAt, updated: reconciliation.updatedAt }} className="mt-3 text-[11px] text-slate-500 flex items-center gap-x-3 gap-y-1 flex-wrap" />
            </div>

            <DialogFooter className="px-6 py-3 bg-slate-50/80 border-t border-slate-200">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
              >
                Đóng
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
